/**
 * Per-Item original/Japanese-translation toggle buttons on a release page
 * (spec-translation.md 表示: 項目単位の切替; task #16).
 *
 * release-lang.ts's `.release-lang-root[data-active-lang]` sets the *page
 * default* display language; this module lets a reader flip a single
 * `.release-item` away from that default independently, without touching
 * any other Item or the page default itself. The effective language for a
 * given Item is: its own `data-lang-override` ("en"|"ja") if present,
 * otherwise the page default (releases.templ / release-lang.ts).
 *
 * Buttons are injected client-side using the same progressive-enhancement
 * pattern as copy-link.ts: only a `.release-item` that actually carries a
 * translation - i.e. has both a `[data-lang="en"]` and a `[data-lang="ja"]`
 * direct child (release_markdown.go's renderItemContentHTML) - gets one. An
 * untranslated Item (single element, no `[data-lang]` at all) gets no
 * button, exactly mirroring the page-level toggle's own restriction
 * (spec-translation.md: "翻訳が無い Item は...切替の対象外"). With no JS, no
 * button is ever injected and no Item ever gains a `data-lang-override`
 * attribute, so every Item just renders under the page's server-rendered
 * en-default CSS exactly as it did before this feature (no-JS parity).
 *
 * Section headings are never touched by this module (only `.release-item`s
 * are queried) - heading translations remain governed solely by the page
 * default (spec-translation.md: "見出し訳は...Item 切替の対象外").
 *
 * --- Display mechanism & why it doesn't fight the page-level CSS ---
 *
 * uno.config.mjs's `release-lang-en` / `release-lang-ja-block` shortcuts
 * key off the page root's `data-active-lang` attribute alone; they know
 * nothing about any one Item's override and must be left alone so the page
 * default keeps working unchanged for every Item that was never
 * individually overridden (design constraint: "既存の CSS バリアント機構は
 * ページ既定用に残す"). So once an Item does have an override, this module
 * never edits those shortcut classes or the root's attribute - it drives
 * the Item's own `[data-lang="en"]` / `[data-lang="ja"]` elements directly,
 * via their `hidden` property *and* an inline `display` declared
 * `!important`.
 *
 * The inline `!important` is required, not decorative: `release-lang-ja-
 * block` itself already needs `!important` to out-cascade the native
 * `[hidden]` attribute's own user-agent rule (modern browsers ship that UA
 * rule as `!important` too - see release-lang.ts's doc comment on the same
 * point), so a plain (non-important) inline override from this module would
 * sometimes lose to that shortcut - e.g. hiding a translated Item's Japanese
 * body via override while the page default is "ja" would be fighting a
 * `!important` class rule with only a normal-priority inline declaration,
 * which loses regardless of specificity (author `!important` always beats
 * author-normal, of either origin). Declaring the inline override
 * `!important` too resolves that tie deterministically in this module's
 * favor: within the same origin/importance tier, an element's own inline
 * style is always treated as having the highest possible specificity, so it
 * wins against any external stylesheet rule - `!important` or not - every
 * time, regardless of the page's current `data-active-lang`. Clearing an
 * override (resyncItemLangToggles) simply removes that one inline
 * declaration and the `data-lang-override` attribute, handing the Item
 * straight back to the untouched page-level CSS mechanism.
 *
 * release-lang.ts's toggleReleaseLang calls resyncItemLangToggles after
 * flipping the page root's `data-active-lang`, clearing every Item's
 * override and relabeling every Item button so the whole page always
 * switches together on that control (spec point: "ページ全体トグル押下時は全
 * override をクリアして一括切替").
 */

const ITEM_SELECTOR = '.release-item';
const EN_SELECTOR = ':scope > [data-lang="en"]';
const JA_SELECTOR = ':scope > [data-lang="ja"]';
const ROOT_SELECTOR = '.release-lang-root';

const BUTTON_CLASS = 'release-item-lang-button';

/** Marks a `.release-item` whose display is currently pinned away from the page default. */
const OVERRIDE_ATTR = 'data-lang-override';

const LANG_EN = 'en';
const LANG_JA = 'ja';

// Short labels naming the language the *next* click would switch to (the
// same "names what's next" convention as release-lang.ts's toggle label,
// just shortened to fit a per-item control: "日本語" ("switch to Japanese")
// / "原文" ("switch back to the original")).
const LABEL_TO_JA = '日本語';
const LABEL_TO_EN = '原文';

interface TranslationElements {
  en: HTMLElement;
  ja: HTMLElement;
}

/**
 * Returns item's `[data-lang="en"]` / `[data-lang="ja"]` direct children, or
 * null if either is missing (an untranslated Item, or malformed markup).
 */
function translationElements(item: HTMLElement): TranslationElements | null {
  const en = item.querySelector<HTMLElement>(EN_SELECTOR);
  const ja = item.querySelector<HTMLElement>(JA_SELECTOR);
  if (!en || !ja) return null;
  return { en, ja };
}

/** Reads the enclosing `.release-lang-root`'s page-default language, defaulting to "en". */
function pageDefaultLang(item: HTMLElement): typeof LANG_EN | typeof LANG_JA {
  const root = item.closest<HTMLElement>(ROOT_SELECTOR);
  return root?.dataset.activeLang === LANG_JA ? LANG_JA : LANG_EN;
}

/** item's own override if set, otherwise the page default. Exported for testing. */
export function effectiveLang(item: HTMLElement): typeof LANG_EN | typeof LANG_JA {
  const override = item.getAttribute(OVERRIDE_ATTR);
  if (override === LANG_EN || override === LANG_JA) return override;
  return pageDefaultLang(item);
}

/**
 * Forces en/ja's visibility to match lang via `hidden` plus an inline
 * `!important` display declaration (see module doc for why the importance
 * is required), independent of the page's current `data-active-lang`.
 */
function forceDisplay(en: HTMLElement, ja: HTMLElement, lang: typeof LANG_EN | typeof LANG_JA): void {
  const showEn = lang === LANG_EN;
  en.hidden = !showEn;
  en.style.setProperty('display', showEn ? 'block' : 'none', 'important');
  ja.hidden = showEn;
  ja.style.setProperty('display', showEn ? 'none' : 'block', 'important');
}

/** Removes item's override, handing its en/ja elements back to page-level CSS control. */
function clearOverride(item: HTMLElement, en: HTMLElement, ja: HTMLElement): void {
  item.removeAttribute(OVERRIDE_ATTR);
  en.style.removeProperty('display');
  ja.style.removeProperty('display');
  // Restore the exact server-rendered hidden state (release_markdown.go
  // never puts `hidden` on the English element and always puts it on the
  // Japanese one); the page-level CSS shortcuts take it from there.
  en.hidden = false;
  ja.hidden = true;
}

function updateButtonLabel(button: HTMLButtonElement, lang: typeof LANG_EN | typeof LANG_JA): void {
  button.textContent = lang === LANG_JA ? LABEL_TO_EN : LABEL_TO_JA;
  button.setAttribute('aria-pressed', String(lang === LANG_JA));
}

function handleClick(item: HTMLElement, button: HTMLButtonElement, en: HTMLElement, ja: HTMLElement): void {
  const next = effectiveLang(item) === LANG_JA ? LANG_EN : LANG_JA;
  item.setAttribute(OVERRIDE_ATTR, next);
  forceDisplay(en, ja, next);
  updateButtonLabel(button, next);
}

function createLangButton(item: HTMLElement, en: HTMLElement, ja: HTMLElement): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = BUTTON_CLASS;
  button.setAttribute('aria-label', 'この項目の表示言語を切り替え');
  updateButtonLabel(button, effectiveLang(item));
  button.addEventListener('click', (event) => {
    // Mirror copy-link.ts: don't let this bubble into the enclosing
    // .release-item's own hover/touch-toggle handling, and suppress any
    // default button behavior.
    event.preventDefault();
    event.stopPropagation();
    handleClick(item, button, en, ja);
  });
  return button;
}

/**
 * Injects a per-Item language-toggle button into every `.release-item` that
 * carries a translation and doesn't already have one. Safe to call multiple
 * times. Exported for testing.
 */
export function initializeReleaseItemLangToggles(): void {
  document.querySelectorAll<HTMLElement>(ITEM_SELECTOR).forEach((item) => {
    if (item.querySelector(`:scope > .${BUTTON_CLASS}`)) return;
    const els = translationElements(item);
    if (!els) return;
    item.appendChild(createLangButton(item, els.en, els.ja));
  });
}

/**
 * Clears every Item's override under root (handing them back to the
 * page-level CSS mechanism) and relabels every Item button to match the
 * (possibly just-changed) page default. Called by release-lang.ts's
 * toggleReleaseLang right after it flips the root's `data-active-lang`, so
 * the page-wide toggle always wins over any per-Item overrides in force
 * (spec point: "ページ全体トグル押下時は全 override をクリアして一括切替").
 * Exported for testing.
 */
export function resyncItemLangToggles(root: ParentNode = document): void {
  root.querySelectorAll<HTMLButtonElement>(`.${BUTTON_CLASS}`).forEach((button) => {
    const item = button.closest<HTMLElement>(ITEM_SELECTOR);
    if (!item) return;
    const els = translationElements(item);
    if (!els) return;

    if (item.hasAttribute(OVERRIDE_ATTR)) {
      clearOverride(item, els.en, els.ja);
    }
    updateButtonLabel(button, effectiveLang(item));
  });
}
